import { ScheduleOutlined as ScheduleOutlinedIcon } from 'components/icons';
import EmptyState from 'components/EmptyState';
import SelectPickerInput from 'components/forms/inputs/rhf-inputs/SelectPickerInput';
import { useDateRangeModal } from 'components/modals/date-range/useDateRangeModal';
import LoadMoreButton from 'components/tables/LoadMoreButton';
import useAuth from 'hooks/useAuth';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useLoadMoreFeed } from 'hooks/useLoadMoreFeed';
import TimecardList from 'pages/timecards/TimecardList';
import { FC, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetTimecardsWithFiltersQuery } from 'store/api/timecards';
import { DashboardForm, TimeRangeOption, timeRangeOptions } from 'types/dashboard';
import { GridLoader } from './CasesTab';
import { getDisplayLabel } from './finalized-hours/util';
import TabContentHeader from './TabContentHeader';

const PAGE_SIZE = 8;

const MyFinalizedTimecards: FC = () => {
    const { control, setValue } = useFormContext<DashboardForm>();
    const effectiveFormat = useEffectiveDateFormat();
    const [dateFrom, dateTo, selectedTimeRange] = useWatch({
        control,
        name: ['dateFrom', 'dateTo', 'selectedTimeRange'],
    });
    const { user } = useAuth();
    const userId = user?.id;
    const [fetchData, { currentData, isFetching, isError }] = useLazyGetTimecardsWithFiltersQuery();

    const fetchPage = useCallback(
        (page: number) => {
            if (!userId) return;
            return fetchData({
                pageSize: PAGE_SIZE,
                page,
                sort: ['timecardDate,desc'],
                filters: `employee.id==(${userId});timecardStatusTypeCode!=(DRAFT);timecardDate=ge=${dateFrom};timecardDate=le=${dateTo}`,
            });
        },
        [fetchData, userId, dateFrom, dateTo]
    );

    const { handleLoadMore } = useLoadMoreFeed({ fetchPage, isLast: currentData?.last });

    const handleRetry = () => {
        void fetchPage(0);
    };

    const { handleSelectChange } = useDateRangeModal<TimeRangeOption>({
        onDateRangeSelected: (option, newDateFrom, newDateTo) => {
            setValue('dateFrom', newDateFrom);
            setValue('dateTo', newDateTo);
            setValue('selectedTimeRange', option);
        },
        initialDateFrom: dateFrom,
        initialDateTo: dateTo,
    });

    const timecardCount = currentData?.totalElements || 0;

    return (
        <div className="flex flex-col">
            <TabContentHeader
                leftContent={
                    // No count until this range has one, and a blank line meanwhile so the rows do not jump.
                    <p className="text-body1">
                        {currentData ? (
                            <FormattedMessage
                                id="dashboard.n-finalized-timecards"
                                values={{
                                    count: timecardCount,
                                    element: (
                                        <span key="element" className="font-semibold">
                                            {timecardCount}
                                        </span>
                                    ),
                                }}
                            />
                        ) : (
                            <>&nbsp;</>
                        )}
                    </p>
                }
                rightContent={
                    <SelectPickerInput
                        control={control}
                        name="selectedTimeRange"
                        options={timeRangeOptions}
                        getOptionIdentifier={(a) => a.value}
                        getOptionLabel={(a) => a.label}
                        getSelectedValueLabel={(option) =>
                            getDisplayLabel(option, selectedTimeRange, dateFrom, dateTo, effectiveFormat)
                        }
                        onChange={handleSelectChange}
                        alwaysTriggerOnChangeForValues={['custom']}
                    />
                }
            />
            <div>
                {/* Order matters: a failed request and a request still in flight must NOT read
                    as tenant-empty. `currentData` holds this range's rows only (RTK's `data` keeps
                    the previous range's), so a new range draws the skeleton and a failed one the
                    error, while Load more, which shares the range's cache entry, keeps the rows.
                    `!currentData` also covers the frame before the mount effect fires the lazy
                    query, where isFetching is still false. */}
                {!currentData?.content.length && isError ? (
                    <EmptyState
                        size="panel"
                        variant="error"
                        onRetry={handleRetry}
                        surface="dashboard-finalised-timecards"
                    />
                ) : (isFetching || !currentData) && !currentData?.content.length ? (
                    <GridLoader height={120} numberOfRows={5} />
                ) : !currentData?.content.length ? (
                    // Used to fall straight through to the list component with an empty array, which
                    // renders nothing at all — a loaded-but-empty card was blank below its header.
                    <EmptyState
                        size="panel"
                        icon={ScheduleOutlinedIcon}
                        titleId="dashboard.empty.finalised-timecards-title"
                        bodyId="dashboard.empty.finalised-timecards-body"
                        surface="dashboard-finalised-timecards"
                    />
                ) : (
                    <TimecardList timecards={currentData?.content || []} compact borderless />
                )}
                {currentData && !currentData.last ? (
                    <LoadMoreButton onClick={handleLoadMore} loading={isFetching} />
                ) : null}
            </div>
        </div>
    );
};

export default MyFinalizedTimecards;
